/**
 * SoftLight — the core "djmsqrvve light": one heavily-blurred radial glow
 * (`glow_soft`, tinted) that slow-drifts on a sine-eased path while its
 * opacity gently breathes. Additive/screen blend so it reads as light, not a
 * blob. This is the atom the whole soft-light look is built from; LightField
 * scatters many, presets pick palettes, scenes compose them.
 *
 * Pixi stays behind the seam: this class only touches the substrate-agnostic
 * ISprite (createSprite + glow_soft + tween/setBlendMode/setTint) — exactly
 * the AtmosphereLayer pattern, so the no-direct-pixi arch-test holds.
 *
 * Render glue is VQA-verified; the sprite-driving LOGIC is unit-tested
 * (SoftLight.test.ts) via an injected sprite factory + FakeSprite.
 *
 * @see ./SPEC.md — language-neutral lift spec (config + algorithm + renderer seam).
 */
import { createSprite } from '../../rendering/utils/Sprite';
import type { ISprite, SpriteOptions } from '../../rendering/IRenderer';
import { Z_LAYERS } from '../../rendering/Z_LAYERS';
import { driftPoint } from './easing';
import type { SoftLightSpec } from './softLightConfig';

export interface SoftLightOptions {
  /** Anchor (world px) the light drifts around. */
  x: number;
  y: number;
  spec: SoftLightSpec;
  /** Breathing phase in [0,1) so a cloud of lights pulses out of sync. */
  phase: number;
  /** Z layer (defaults to ATMOSPHERE so lights sit behind entities). */
  depth?: number;
  /** Optional display parent (Scene FX v3 M1) — the sprite adds itself to this
   *  container (a per-layer WorldLayerHandle) so AtmosphereLayer dims the whole
   *  layer via container.alpha. */
  parent?: { addChild(child: unknown): void };
  /** Injectable for tests; defaults to the real createSprite. May return null
   *  when the glow texture isn't registered yet (best-effort, never throws). */
  spriteFactory?: (opts: SpriteOptions) => ISprite | null;
  /** Hold still (reduced motion): no drift, alpha midway through the breathe. */
  still?: boolean;
}

export class SoftLight {
  private sprite: ISprite | null = null;
  private destroyed = false;

  constructor(opts: SoftLightOptions) {
    const factory = opts.spriteFactory ?? ((o: SpriteOptions) => createSprite(null, o));
    const spec = opts.spec;

    const sprite = factory({
      x: opts.x,
      y: opts.y,
      initialTextureKey: 'glow_soft',
      width: spec.size,
      height: spec.size,
      depth: opts.depth ?? Z_LAYERS.ATMOSPHERE,
      alpha: spec.alphaMin, // start at the breathing floor
      parent: opts.parent,
    });
    if (!sprite) return; // texture not ready — quieter scene, never a crash
    this.sprite = sprite;

    sprite.setTint(spec.color);
    sprite.setBlendMode(spec.blendMode);
    if (opts.still) {
      sprite.setAlpha((spec.alphaMin + spec.alphaMax) / 2);
      return;
    }

    // Drift: ease out to a bounded far point on a fixed bearing, yoyo back.
    // Phase 1.0 of the cycle is the far extent of the drift.
    const far = driftPoint({ x: opts.x, y: opts.y }, { radius: spec.driftRadius, angleRad: spec.driftAngleRad }, 1);
    sprite.tween(
      { x: far.x, y: far.y },
      { durationMs: spec.periodMs, ease: 'sineInOut', yoyo: true, repeat: -1 },
    );

    // Breathing opacity floor↔ceiling. A per-light phase offset (mapped onto
    // the period) keeps the cloud from pulsing in unison.
    sprite.tween(
      { alpha: spec.alphaMax },
      { durationMs: spec.periodMs * (0.5 + opts.phase * 0.5), ease: 'sineInOut', yoyo: true, repeat: -1 },
    );
  }

  /** Whether the light actually mounted (false if the texture wasn't ready). */
  isAlive(): boolean {
    return !this.destroyed && this.sprite !== null;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.sprite) {
      try { this.sprite.killAllTweens(); } catch { /* already gone */ }
      try { this.sprite.destroy(); } catch { /* already gone */ }
      this.sprite = null;
    }
  }
}
